import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { gradientPresets, photoWallpapers, DEFAULT_WALLPAPER } from '../theme/wallpapers'
import { iconThemes, DEFAULT_ICON_THEME } from '../theme/iconThemes'

const ThemeContext = createContext(null)

const LS_KEYS = {
  WALLPAPER_KIND: 'pp_wallpaper_kind',
  WALLPAPER_VALUE: 'pp_wallpaper_value',
  ICON_THEME: 'pp_icon_theme',
  ICON_USER_COLOR: 'pp_icon_user_color'
}

const findGradient = (id) => gradientPresets.find(g => g.id === id)
const findPhoto = (id) => photoWallpapers.find(p => p.id === id)
const findIconTheme = (id) => iconThemes.find(t => t.id === id) || iconThemes[0]

// Simple string hash for deterministic color selection
const hashString = (str) => {
  let h = 0
  for (let i = 0; i < str.length; i++) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0
  return Math.abs(h)
}

export const ThemeProvider = ({ children }) => {
  // Wallpaper
  const [wallpaperKind, setWallpaperKind] = useState(() => localStorage.getItem(LS_KEYS.WALLPAPER_KIND) || DEFAULT_WALLPAPER.kind)
  const [wallpaperValue, setWallpaperValue] = useState(() => localStorage.getItem(LS_KEYS.WALLPAPER_VALUE) || DEFAULT_WALLPAPER.value)
  // Icon theme
  const [iconThemeId, setIconThemeId] = useState(() => localStorage.getItem(LS_KEYS.ICON_THEME) || DEFAULT_ICON_THEME)
  const [userIconColor, setUserIconColor] = useState(() => localStorage.getItem(LS_KEYS.ICON_USER_COLOR) || '#f472b6')
  const themeChangeCounter = useRef(0)

  // Derived data
  const iconTheme = findIconTheme(iconThemeId)
  const palette = iconTheme.id === 'userColor' ? [userIconColor] : iconTheme.colors

  const setWallpaper = useCallback((kind, value) => {
    setWallpaperKind(kind)
    setWallpaperValue(value)
    localStorage.setItem(LS_KEYS.WALLPAPER_KIND, kind)
    localStorage.setItem(LS_KEYS.WALLPAPER_VALUE, value)
    window.dispatchEvent(new CustomEvent('wallpaperChange', { detail: { kind, value } }))
  }, [])

  const setIconTheme = useCallback((id) => {
    setIconThemeId(id)
    localStorage.setItem(LS_KEYS.ICON_THEME, id)
    themeChangeCounter.current++
    window.dispatchEvent(new CustomEvent('iconThemeChange', { detail: { id, counter: themeChangeCounter.current } }))
  }, [])

  const updateUserColor = useCallback((color) => {
    setUserIconColor(color)
    localStorage.setItem(LS_KEYS.ICON_USER_COLOR, color)
    if (iconThemeId === 'userColor') {
      themeChangeCounter.current++
      window.dispatchEvent(new CustomEvent('iconThemeChange', { detail: { id: 'userColor', color, counter: themeChangeCounter.current } }))
    }
  }, [iconThemeId])

  // One-time default transition to auroraDream & neon
  useEffect(() => {
    if (!localStorage.getItem('pp_default_theme_applied_v1')) {
      localStorage.setItem('pp_default_theme_applied_v1', 'true')
      setWallpaper(DEFAULT_WALLPAPER.kind, DEFAULT_WALLPAPER.value)
      setIconTheme(DEFAULT_ICON_THEME)
    }
  }, [setWallpaper, setIconTheme])

  // Deterministic color by name
  const colorForName = useCallback((name, offset = 0) => {
    if (!palette.length) return '#ffffff'
    const h = hashString(name + ':' + offset)
    return palette[h % palette.length]
  }, [palette])

  const value = {
    wallpaperKind,
    wallpaperValue,
    setWallpaper,
    iconThemeId,
    setIconTheme,
    userIconColor,
    updateUserColor,
    palette,
    iconTheme,
    colorForName,
    gradientPresets,
    photoWallpapers,
    iconThemes
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export const useTheme = () => useContext(ThemeContext)
